import React from "react"
import { format } from "date-fns"
import { Check } from "lucide-react"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { getDateFnsLocale } from "@/lib/i18n/format"
import {
  LegalCounter,
  LegalCounters,
  WeeklyRestRecord,
} from "@/reducers/tachosSlice"

// The dispatcher's own telematics panel ("remaining driving and resting
// times"), one card at a time: every counter with the three columns the
// panel shows and a meter of how much of the limit is used up.

type Unit = "duration" | "days"

// A maximum the driver must stay under (driving, amplitude, work week) or
// a minimum the rest must reach.
type Kind = "max" | "min"

type Row = {
  label: string
  counter: LegalCounter | null
  kind: Kind
  unit?: Unit
  note?: string
}

// Same output as formatDuration in utils/formatUtils ("1 234h 5m") in legacy
// and English; the units move into the translation for Polish.
const formatCounterDuration = (t: TFunction, seconds: number): string =>
  t("reports.legalCounters.duration", {
    hours: Math.floor(seconds / 3600)
      .toLocaleString("en-US")
      .replace(/,/g, " "),
    minutes: Math.floor((seconds % 3600) / 60),
  })

const formatValue = (
  t: TFunction,
  value: number | null,
  unit: Unit,
): string => {
  if (value === null) return "-"
  if (unit === "days") return t("reports.legalCounters.days", { count: value })
  return formatCounterDuration(t, value)
}

const formatDateTime = (iso: string | null): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return format(date, "dd.MM.yyyy HH:mm", { locale: getDateFnsLocale() })
}

const usedRatio = (counter: LegalCounter): number => {
  if (!counter.limit || counter.total === null) return 0
  return Math.min(Math.max(counter.total / counter.limit, 0), 1)
}

// A maximum is fine until it runs out; a minimum is a debt until it is met.
const isSettled = (counter: LegalCounter, kind: Kind): boolean =>
  kind === "max" ? (counter.remaining ?? 1) > 0 : counter.remaining === 0

const Meter: React.FC<{ counter: LegalCounter; kind: Kind }> = ({
  counter,
  kind,
}) => {
  const settled = isSettled(counter, kind)
  const fill =
    kind === "max"
      ? settled
        ? "bg-blue-500"
        : "bg-amber-500"
      : settled
        ? "bg-emerald-500"
        : "bg-amber-500"
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
      role="presentation"
    >
      <div
        className={cn("h-full rounded-full", fill)}
        style={{ width: `${usedRatio(counter) * 100}%` }}
      />
    </div>
  )
}

const CounterRow: React.FC<Row> = ({
  label,
  counter,
  kind,
  unit = "duration",
  note,
}) => {
  const { t } = useTranslation()
  if (!counter) return null
  const settled = isSettled(counter, kind)
  return (
    <tr className="border-b border-border last:border-b-0">
      <th
        scope="row"
        className="py-1.5 pr-2 text-left text-xs font-normal text-foreground"
      >
        {label}
        {note && (
          <span className="block text-[10px] text-muted-foreground">
            {note}
          </span>
        )}
      </th>
      <td className="whitespace-nowrap px-2 py-1.5 text-right text-xs tabular-nums text-muted-foreground">
        {formatValue(t, counter.total, unit)}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 text-right text-xs tabular-nums text-muted-foreground">
        {formatValue(t, counter.limit, unit)}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 text-right text-xs font-medium tabular-nums text-foreground">
        <span className="inline-flex items-center justify-end gap-1">
          {kind === "min" && settled && (
            <Check
              className="h-3 w-3 text-emerald-600"
              aria-label={t("reports.legalCounters.requirementMet")}
            />
          )}
          {formatValue(t, counter.remaining, unit)}
        </span>
      </td>
      <td className="w-[28%] min-w-[80px] py-1.5 pl-2">
        <Meter counter={counter} kind={kind} />
      </td>
    </tr>
  )
}

const Section: React.FC<{
  title: string
  limitHeader: string
  children: React.ReactNode
}> = ({ title, limitHeader, children }) => {
  const { t } = useTranslation()
  return (
    <table className="w-full border-collapse">
      <caption className="pb-1 text-left text-xs font-semibold text-foreground">
        {title}
      </caption>
      <thead>
        <tr className="border-b border-border text-[10px] uppercase tracking-wide text-muted-foreground">
          <th scope="col" className="py-1 pr-2 text-left font-medium">
            {t("reports.legalCounters.columns.counter")}
          </th>
          <th scope="col" className="px-2 py-1 text-right font-medium">
            {t("reports.legalCounters.columns.used")}
          </th>
          <th scope="col" className="px-2 py-1 text-right font-medium">
            {limitHeader}
          </th>
          <th scope="col" className="px-2 py-1 text-right font-medium">
            {t("reports.legalCounters.columns.remaining")}
          </th>
          <th scope="col" className="py-1 pl-2">
            <span className="sr-only">
              {t("reports.legalCounters.columns.shareUsed")}
            </span>
          </th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}

const weeklyRestPlace = (
  t: TFunction,
  rest: WeeklyRestRecord,
): string | null => {
  if (!rest.country) return null
  if (rest.atHome === null) return rest.country
  return rest.atHome
    ? t("reports.legalCounters.atHome", { country: rest.country })
    : t("reports.legalCounters.away", { country: rest.country })
}

const weeklyRestNote = (
  t: TFunction,
  rest: WeeklyRestRecord,
): string | undefined => {
  const place = weeklyRestPlace(t, rest)
  const deadline = formatDateTime(rest.compensationDeadline)
  if (deadline && (rest.remaining ?? 0) > 0) {
    return place
      ? t("reports.legalCounters.placeCompensateBy", { place, deadline })
      : t("reports.legalCounters.compensateBy", { deadline })
  }
  return place ?? undefined
}

export const LegalCountersPanel: React.FC<{ counters: LegalCounters }> = ({
  counters,
}) => {
  const { t } = useTranslation()
  const workWeekEnd = formatDateTime(counters.workWeekEnd)
  return (
    <div className="flex flex-col gap-4">
      <Section
        title={t("reports.legalCounters.sections.driving")}
        limitHeader={t("reports.legalCounters.columns.max")}
      >
        <CounterRow
          label={t("reports.legalCounters.rows.continuousDriving")}
          counter={counters.continuousDriving}
          kind="max"
        />
        <CounterRow
          label={t("reports.legalCounters.rows.dailyDriving")}
          counter={counters.dailyDriving}
          kind="max"
        />
        <CounterRow
          label={t("reports.legalCounters.rows.dailyAmplitude")}
          counter={counters.dailyAmplitude}
          kind="max"
        />
        <CounterRow
          label={t("reports.legalCounters.rows.reducedDailyRests")}
          counter={counters.reducedDailyRestDays}
          kind="max"
          unit="days"
        />
        <CounterRow
          label={t("reports.legalCounters.rows.extendedDrivingDays")}
          counter={counters.extendedDrivingDays}
          kind="max"
          unit="days"
        />
        <CounterRow
          label={t("reports.legalCounters.rows.weeklyDriving")}
          counter={counters.weeklyDriving}
          kind="max"
        />
        <CounterRow
          label={t("reports.legalCounters.rows.biweeklyDriving")}
          counter={counters.biweeklyDriving}
          kind="max"
        />
        <CounterRow
          label={t("reports.legalCounters.rows.consecutiveDrivingDays")}
          counter={counters.consecutiveDrivingDays}
          kind="max"
          unit="days"
        />
        <CounterRow
          label={t("reports.legalCounters.rows.workWeek")}
          counter={counters.workWeek}
          kind="max"
          note={
            workWeekEnd
              ? t("reports.legalCounters.workWeekEnds", { date: workWeekEnd })
              : undefined
          }
        />
      </Section>
      <Section
        title={t("reports.legalCounters.sections.rest")}
        limitHeader={t("reports.legalCounters.columns.min")}
      >
        <CounterRow
          label={t("reports.legalCounters.rows.middayBreak")}
          counter={counters.middayBreak}
          kind="min"
        />
        <CounterRow
          label={t("reports.legalCounters.rows.dailyRest")}
          counter={counters.dailyRest}
          kind="min"
        />
        <CounterRow
          label={t("reports.legalCounters.rows.previousDailyRest")}
          counter={counters.previousDailyRest}
          kind="min"
        />
      </Section>
      {(counters.weeklyRestCompensation || counters.weeklyRests.length > 0) && (
        <Section
          title={t("reports.legalCounters.sections.weeklyRest")}
          limitHeader={t("reports.legalCounters.columns.min")}
        >
          <CounterRow
            label={t("reports.legalCounters.rows.weeklyRestCompensation")}
            counter={counters.weeklyRestCompensation}
            kind="min"
          />
          {counters.weeklyRests.map((rest) => (
            <CounterRow
              key={rest.weeksAgo}
              label={t("reports.legalCounters.rows.weeklyRestWeek", {
                weeksAgo: rest.weeksAgo,
              })}
              note={weeklyRestNote(t, rest)}
              counter={{
                total: rest.total,
                limit: rest.limit,
                remaining: rest.remaining,
              }}
              kind="min"
            />
          ))}
        </Section>
      )}
    </div>
  )
}

export default LegalCountersPanel
